/**
 * Schemas Zod dos cinco catálogos (`03-endpoints-draft.md §4`).
 *
 * O corpo é **um só**, com os campos extras opcionais, e o caso de uso recusa campo
 * que não pertence ao catálogo da rota (`cor` em `/tags`, por exemplo). A alternativa
 * — cinco schemas — daria cinco lugares para corrigir a mesma validação de título.
 *
 * Não existe `DELETE` em nenhum dos cinco (RN-41): registro em uso nunca é
 * excluído. Desativar é a operação, e o histórico já gravado mantém o título antigo,
 * porque as FKs apontam para a linha e não para o texto.
 */
import { z } from 'zod'

import { zBooleanoFiltro, zBusca, zIdCatalogo } from '../comum/campos'
import { schemaPaginacaoOffset } from '../comum/paginacao'
import { zOrdem, zTituloOrganizacao } from '../organizacao/schemas'

/** `motivos_agendamento.cor` — hex `#RRGGBB`, espelhando `motivos_agendamento_ck_cor`. */
export const zCorHex = z
  .string()
  .trim()
  .regex(/^#[0-9A-Fa-f]{6}$/, 'Informe a cor no formato #RRGGBB.')
  .transform((valor) => valor.toUpperCase())

/** `produtos_associados.codigo_referencia` — código do empreendimento (TELA-14). */
export const zCodigoReferencia = z
  .string()
  .trim()
  .min(1, 'Informe o código de referência ou deixe em branco.')
  .max(60, 'Código de referência muito longo.')

const camposExtras = {
  /** Só `motivos-agendamento`. Padrão igual ao do banco. */
  cor: zCorHex.optional(),
  /** Só `motivos-agendamento` (TELA-12). */
  contabilizarRelatorio: z.boolean().optional(),
  /** Só `produtos-associados`. `null` limpa o código. */
  codigoReferencia: zCodigoReferencia.nullish(),
}

export const schemaCriarItemCatalogo = z
  .object({
    titulo: zTituloOrganizacao,
    ordem: zOrdem.default(0),
    ...camposExtras,
  })
  .strict()

export type EntradaCriarItemCatalogo = z.infer<typeof schemaCriarItemCatalogo>

/**
 * `ativo` fora do `PATCH` de propósito: desativar e reativar têm rota própria
 * (`POST /{id}/desativar`, `/reativar`) porque cada uma tem consequência diferente —
 * desativar tira o item dos seletores sem tocar no histórico; reativar faz o título
 * voltar a disputar a unicidade do RN-42 e pode colidir. Um `PATCH { ativo }`
 * genérico esconderia as duas conferências.
 */
export const schemaAtualizarItemCatalogo = z
  .object({
    titulo: zTituloOrganizacao.optional(),
    ordem: zOrdem.optional(),
    ...camposExtras,
  })
  .strict()
  .refine((valor) => Object.keys(valor).length > 0, 'Informe ao menos um campo para alterar.')

export type EntradaAtualizarItemCatalogo = z.infer<typeof schemaAtualizarItemCatalogo>

/**
 * `PATCH /{catalogo}/ordem` — reordenação em lote.
 *
 * Uma requisição para toda a lista, e não um `PATCH` por item: reordenar arrastando
 * muda a posição de N itens de uma vez, e N requisições deixariam a lista em ordem
 * inconsistente se uma delas falhasse no meio. Aqui é uma transação.
 */
export const schemaReordenarCatalogo = z
  .object({
    itens: z
      .array(z.object({ id: zIdCatalogo, ordem: zOrdem }).strict())
      .min(1, 'Informe ao menos um item.')
      .max(500, 'Seleção acima do máximo aceito em uma requisição.'),
  })
  .strict()
  .superRefine((valor, contexto) => {
    const ids = valor.itens.map((item) => item.id)
    if (new Set(ids).size !== ids.length) {
      contexto.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['itens'],
        message: 'O mesmo item aparece duas vezes na lista.',
      })
    }
  })

export type EntradaReordenarCatalogo = z.infer<typeof schemaReordenarCatalogo>

export const CAMPOS_ORDENACAO_CATALOGO = ['ordem', 'titulo', 'criadoEm'] as const

export type CampoOrdenacaoCatalogo = (typeof CAMPOS_ORDENACAO_CATALOGO)[number]

/**
 * `?ativo=true` é o modo que as telas de operação usam para preencher select (§4).
 * Um select que oferece motivo desativado produz `422 MOTIVO_INATIVO` no submit
 * (RN-11A) — e o atendente não tem como saber por quê.
 */
export const schemaFiltrosCatalogo = schemaPaginacaoOffset.extend({
  busca: zBusca,
  ativo: zBooleanoFiltro.optional(),
})

export type FiltrosCatalogo = z.infer<typeof schemaFiltrosCatalogo>
